import { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import store from './app/store';
import { Provider } from 'react-redux';
import SuspenseContent from './containers/SuspenseContent';
import 'react-tooltip/dist/react-tooltip.css';
import { initializeSentry } from './utils/sentry';
import { setupSentryStoreSubscriber } from './utils/sentryStoreSubscriber';
import { logVersionInfo } from './utils/versionInfo';

// Log version information for debugging
import { initPostHog } from './utils/posthog';

// Initialize Sentry for error tracking
initializeSentry(store);

// Set up Redux store subscriber to update Sentry organization context
setupSentryStoreSubscriber(store);

logVersionInfo();

initPostHog();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
	<Suspense fallback={<SuspenseContent />}>
		<Provider store={store}>
			<App />
		</Provider>
	</Suspense>
);

// Register the unified service worker (PWA + Firebase messaging)
if ('serviceWorker' in navigator) {
	window.addEventListener('load', async () => {
		try {
			console.log('Registering unified service worker...');
			const registration = await navigator.serviceWorker.register(
				'/service-worker.js',
				{
					scope: '/',
					// CRITICAL: Disable HTTP cache for service worker file
					// This ensures iOS Safari and other browsers always fetch the latest SW
					updateViaCache: 'none',
				}
			);

			console.log(
				'Service worker registered successfully:',
				registration.scope
			);

			// Track if we're currently reloading to prevent loops
			let isReloading = false;

			// Function to handle service worker updates automatically
			const handleUpdate = (newWorker) => {
				if (!newWorker) return;

				newWorker.addEventListener('statechange', () => {
					if (
						newWorker.state === 'installed' &&
						navigator.serviceWorker.controller
					) {
						console.log(
							'New service worker installed, activating immediately...'
						);
						// AUTOMATIC UPDATE: Tell the new service worker to skip waiting
						// This ensures users always get the latest version without prompts
						newWorker.postMessage({ type: 'SKIP_WAITING' });
					}
				});
			};

			// Listen for service worker updates
			registration.addEventListener('updatefound', () => {
				console.log('Service worker update found');
				const newWorker = registration.installing;
				handleUpdate(newWorker);
			});

			// CRITICAL: Listen for controller changes and reload automatically
			// This fires when a new service worker takes control
			navigator.serviceWorker.addEventListener('controllerchange', () => {
				console.log('Service worker controller changed');
				// Prevent reload loops by checking if we're already reloading
				if (!isReloading) {
					isReloading = true;
					console.log('Reloading to activate new service worker...');
					// Small delay to ensure service worker is fully ready
					setTimeout(() => {
						window.location.reload();
					}, 100);
				}
			});

			// AUTOMATIC UPDATE CHECK: Check for updates every 60 seconds
			// More aggressive than the 30s check in the service worker itself
			setInterval(() => {
				console.log('Checking for service worker updates...');
				registration.update().catch((err) => {
					console.error('Update check failed:', err);
				});
			}, 60000);

			// CRITICAL FOR MOBILE: Check for updates when app becomes visible
			// iOS and Android often suspend timers when app is backgrounded
			document.addEventListener('visibilitychange', () => {
				if (!document.hidden) {
					console.log('App became visible, checking for updates...');
					registration.update().catch((err) => {
						console.error('Visibility update check failed:', err);
					});
				}
			});

			// CRITICAL FOR MOBILE: Check for updates when window gains focus
			// Catches cases where user returns to an already-open PWA
			window.addEventListener('focus', () => {
				console.log('Window gained focus, checking for updates...');
				registration.update().catch((err) => {
					console.error('Focus update check failed:', err);
				});
			});

			// Initial update check after registration
			// Ensures we catch any updates that happened while app was closed
			setTimeout(() => {
				console.log('Running initial update check...');
				registration.update().catch((err) => {
					console.error('Initial update check failed:', err);
				});
			}, 5000);

			// Listen for messages from the service worker
			navigator.serviceWorker.addEventListener('message', (event) => {
				if (event.data.action === 'reloadPage') {
					console.log('Reloading page as requested by service worker');
					window.location.reload();
				}
			});
		} catch (error) {
			console.error('Service worker registration failed:', error);
		}
	});
}
